import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useModalStore } from '../../stores/modal.store';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { Avatar } from '../../components/ui/Avatar';
import { KPI } from '../../components/ui/KPI';
import { EmptyState } from '../../components/ui/EmptyState';
import { Helper } from '../../components/ui/Helper';
import { SectionMark } from '../../components/ui/SectionMark';
// Telefono: i filtri finiscono dietro un solo tasto. Sul computer questo
// componente restituisce i suoi figli così come sono, quindi qui non cambia niente.
import { MobileFilters } from '../../components/ui/MobileFilters';
// Quello che dal telefono non si fa (qui: la matrice dei permessi) diventa una riga onesta.
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtDate } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SettingsMobile } from './SettingsMobile';
import { RuoliSemplici } from './RuoliSemplici';
import { AssistenteCard } from './AssistenteCard';
import './settings.css';

interface Role {
  id: string; key: string | null; name: string; color: string; shortLabel: string;
  canSeeMargin: boolean; isSystem: boolean; usersCount: number;
  permissions: Array<{ module: string; level: string }>;
}

interface UserItem {
  id: string; email: string; firstName: string; lastName: string;
  initials: string; avatarColor: string; active: boolean;
  role: { id: string; name: string; color: string; shortLabel: string; key: string | null };
}

interface OrgSettings {
  organizationName: string; logoUrl: string | null; primaryColor: string;
  marginThreshold: string; currency: string; timezone: string;
  dateFormat: string; docLanguage: string; fiscalYearStart: number;
}

interface Integration {
  id: string; kind: string; name: string; status: string;
  config: any; lastSyncAt: string | null; hasKey: boolean;
  connectedBy: { firstName: string; lastName: string } | null;
}

/** I moduli coi nomi della barra laterale (prima: enum inglesi in maiuscolo). */
const MODULE_LABEL: Record<string, string> = {
  DASHBOARD: 'Oggi', TASKS: 'Task', TRATTATIVE: 'Trattative', CRM: 'Contatti e progetti',
  AMMINISTRAZIONE: 'Amministrazione', FATTURAZIONE: 'Fatture e preventivi', SERVICES: 'Abbonamenti',
  MARKETING: 'Digital Marketing', PRODUCT: 'Digital Product', DESIGN: 'Digital Design',
  TALENT: 'Talent Sourcing', SUPPLIERS: 'Professionisti', VAULT: 'Password Vault', REPORT: 'Report',
  SETTINGS: 'Impostazioni', MARGIN: 'Margini', AGENTI: 'Agenti',
};


const KIND_LABELS: Record<string, string> = {
  FATTURA24: 'Fattura24', SLACK: 'Slack', FIGMA: 'Figma', GITHUB: 'GitHub',
  META_ADS: 'Meta Ads', GOOGLE_CALENDAR: 'Google Calendar', ZOOM: 'Zoom',
  HARVEST: 'Harvest', OTHER: 'Altro',
};

// Solo display: l'enum resta invariato lato API
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Attiva', ERROR: 'Errore', DISCONNECTED: 'Disconnessa',
};

type Tab = 'ruoli' | 'utenti' | 'preferenze' | 'integrazioni';

// Sub-nav raggruppata (layout reference a 2 colonne)
const NAV: Array<{ kicker: string; items: Array<{ k: Tab; label: string; icon: IconName }> }> = [
  { kicker: 'L’azienda',          items: [{ k: 'preferenze', label: 'Dati e preferenze', icon: 'building' }] },
  { kicker: 'Persone e permessi', items: [
      { k: 'ruoli',  label: 'Ruoli: chi vede cosa', icon: 'lock' },
      { k: 'utenti', label: 'Utenti',           icon: 'users' } ] },
  { kicker: 'Servizi collegati',  items: [{ k: 'integrazioni', label: 'Banca, fatture e altri', icon: 'link' }] },
];

/** L'azione principale della scheda aperta: una sola, e sta scritta una volta
 *  sola perché compare in due posti (in alto sul computer, in fondo sul telefono). */
// Niente "Nuovo ruolo": i ruoli sono quattro e fissi (lib/ruoli.ts, 28/10/2026).
const AZIONE: Partial<Record<Tab, { label: string; modal: 'newUser' | 'newIntegration' }>> = {
  utenti:       { label: 'Nuovo utente',      modal: 'newUser' },
  integrazioni: { label: 'Collega un servizio', modal: 'newIntegration' },
};

/**
 * Sul telefono le Impostazioni sono SettingsMobile: un elenco di voci come
 * quelle dell'iPhone, ognuna con la sua pagina. Stesse chiamate; sul computer
 * resta questa pagina, identica.
 */
export function SettingsPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><SettingsMobile /></div> : <SettingsDesktop />;
}

function SettingsDesktop() {
  // ?voce=… come sul telefono: gli avvisi dei collegamenti fermi portano dritti a "Servizi collegati".
  const voceUrl = new URLSearchParams(window.location.search).get('voce') ?? '';
  const [tab, setTab] = useState<Tab>((['ruoli', 'utenti', 'preferenze', 'integrazioni'] as string[]).includes(voceUrl) ? (voceUrl as Tab) : 'utenti');
  const openModal = useModalStore((s) => s.open);
  const voce = AZIONE[tab];
  const azione = voce ? { label: voce.label, apri: () => openModal(voce.modal) } : null;

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora,
            niente icon-tile — il layout a 2 colonne sotto resta invariato ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="st2-hd">
          <SectionMark icon="settings" className="st2-sez" />
          <div>
            <h1 className="ed-title">Impostazioni</h1>
            <div className="ed-sub st2-sub">Chi entra nel gestionale, cosa può vedere e quali servizi sono collegati.</div>
          </div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo alla pagina. */}
        <div className="flex gap-2 pb-1 d-only">
          {azione && <button className="btn btn-primary" onClick={azione.apri}><Icon name="plus" /> {azione.label}</button>}
        </div>
      </div>

      <div className="settings-layout">
        <aside className="settings-nav card">
          {NAV.map((g) => (
            <div key={g.kicker} className="settings-nav-group">
              <div className="settings-nav-kicker">{g.kicker}</div>
              {g.items.map((it) => (
                <button key={it.k} type="button" className="settings-nav-item" aria-selected={tab === it.k} onClick={() => setTab(it.k)}>
                  <span className="settings-nav-ico"><Icon name={it.icon} size={14} /></span>
                  {it.label}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <div className="min-w-0">
          {tab === 'ruoli'        && <RuoliSemplici />}
          {tab === 'utenti'       && <UsersTab />}
          {tab === 'preferenze'   && <PreferencesTab />}
          {tab === 'integrazioni' && <IntegrationsTab />}
        </div>
      </div>

      {/* Telefono: la stessa azione della scheda aperta, dove il pollice arriva. */}
      {azione && (
        <DesktopOnly
          cosa={`La configurazione: ${azione.label.toLowerCase()}`}
          perche="utenti, ruoli e collegamenti esterni richiedono verifiche e restano sul computer; qui puoi consultarne lo stato."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={azione.apri}><Icon name="plus" /> {azione.label}</button>
          </div>
        </DesktopOnly>
      )}
    </div>
  );
}

// =====================================================================
// UTENTI
// =====================================================================
function UsersTab() {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    queryKey: ['users', search],
    queryFn: () => api.get<{ items: UserItem[] }>(`/users?pageSize=200${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  });

  // Query separata senza search: serve per i contatori totali/attivi nel filterbar,
  // che devono restare stabili anche quando l'utente sta filtrando la lista.
  const { data: allData } = useQuery({
    queryKey: ['users', 'stats'],
    queryFn: () => api.get<{ items: UserItem[] }>('/users?pageSize=200'),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.patch(`/users/${id}`, { active }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.ok(vars.active ? 'Utente attivato' : 'Utente disattivato');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const items = data?.items ?? [];
  const allItems = allData?.items ?? [];

  const attivi = allItems.filter((u) => u.active).length;
  const plur = (n: number, uno: string, molti: string) => `${n} ${n === 1 ? uno : molti}`;

  return (
    // Niente space-y-6: vedi la nota in RolesTab. Il ritmo lo danno mb-4 sulla
    // barra filtri e .ed-section.
    <div>
      <MobileFilters attivi={search.trim() ? 1 : 0}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {/* Le stesse misure di flex delle altre barre filtri dell'app: il
            width: 280 scritto qui prima non faceva niente, .tb-search ha
            flex: 1 e vinceva quello. */}
        <div className="tb-search" style={{ flex: '1 1 150px', minWidth: 150, maxWidth: 300, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca un utente" placeholder="Cerca per nome o email…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="spacer" />
        <span className="mute text-[12px]">
          {search
            ? `${plur(items.length, 'risultato', 'risultati')} · ${plur(allItems.length, 'utente in tutto', 'utenti in tutto')} (${attivi} attiv${attivi === 1 ? 'o' : 'i'})`
            : `${plur(allItems.length, 'utente', 'utenti')} · ${attivi} attiv${attivi === 1 ? 'o' : 'i'}`}
        </span>
      </div>
      </MobileFilters>

      {/* Editoriale: lista utenti nuda sul foglio, kicker + conteggio mono.
          La CTA "Nuovo utente" resta nell'header di pagina. */}
      <div className="ed-section">
        <div className="data-hd">
          <span className="tk-sez-t">Utenti</span>
          <span className="mono mute text-[12px]">{items.length}</span>
        </div>
        <div className="m-desk-note m-only" role="note">
          <Icon name="help" size={14} />
          <span><strong>Modifica, password e attivazione degli utenti</strong> si fanno dal computer: qui restano anagrafica, ruolo e stato da consultare.</span>
        </div>
        {/* tbl--card: sotto i 768px ogni riga diventa una scheda (os-planning/mobile.md) */}
        <table className="tbl tbl--ed tbl--card tbl--static">
          <thead>
            <tr><th>Utente</th><th style={{ width: 220 }}>Ruolo</th><th style={{ width: 160 }}>Stato</th><th style={{ width: 150 }}>Azioni</th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td data-m="hd">
                  <div className="row" style={{ gap: 10 }}>
                    <Avatar initials={u.initials} />
                    <div className="cell-2l min-w-0">
                      <div className="p truncate">{u.firstName} {u.lastName}</div>
                      <div className="s mono truncate" title={u.email}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td data-m="key" data-l="Ruolo">
                  <div className="row" style={{ gap: 6 }}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: u.role.color }} />
                    {u.role.name}
                  </div>
                </td>
                <td data-m="key" data-l="Stato">
                  <Chip tone={u.active ? 'ok' : 'ghost'} dot>{u.active ? 'Attivo' : 'Disattivato'}</Chip>
                </td>
                {/* data-m="act": sulla scheda i tre tasti si ancorano in alto a
                    destra e tolgono il chevron, che qui mentirebbe (una riga
                    utente non apre nessuna pagina). m-hit porta il bersaglio a
                    44px solo sul telefono: sul computer .m-hit non esiste. */}
                <td data-m="off" className="d-only">
                  <div className="flex gap-1">
                    <button className="btn btn-sm btn-ghost m-hit" onClick={() => openModal('newUser', { user: u })} aria-label={`Modifica ${u.firstName} ${u.lastName}`} title="Modifica">
                      <Icon name="more" size={12} />
                    </button>
                    <button className="btn btn-sm btn-ghost m-hit" onClick={() => openModal('password', { userId: u.id, userName: `${u.firstName} ${u.lastName}`, mode: 'admin' })} aria-label="Reimposta password" title="Reimposta password">
                      <Icon name="refresh" size={12} />
                    </button>
                    <button className="btn btn-sm btn-ghost m-hit" onClick={() => toggleActive.mutate({ id: u.id, active: !u.active })} aria-label={u.active ? 'Disattiva' : 'Attiva'} title={u.active ? 'Disattiva' : 'Attiva'}>
                      <Icon name={u.active ? 'lock' : 'check'} size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={4} style={{ padding: 0 }}>
                  <EmptyState
                    icon="users"
                    title={search ? 'Nessun utente trovato' : 'Nessun utente'}
                    description={
                      search
                        ? 'Prova a modificare la ricerca.'
                        : 'Invita i membri del team per dare loro accesso a Masamune OS.'
                    }
                    action={!search ? (
                      <button className="btn btn-primary d-only" onClick={() => openModal('newUser')}>
                        <Icon name="plus" size={12} /> Nuovo utente
                      </button>
                    ) : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// PREFERENZE
// =====================================================================
const MOSTRA_ANTEPRIMA = false as boolean;

function PreferencesTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['org-settings'], queryFn: () => api.get<OrgSettings>('/settings/org') });
  const [form, setForm] = useState<Partial<OrgSettings>>({});
  const [dirty, setDirty] = useState(false);

  const update = (k: keyof OrgSettings, v: any) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: () => api.patch('/settings/org', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-settings'] });
      setDirty(false);
      setForm({});
      toast.ok('Preferenze salvate');
    },
    onError: (e: any) => toast.alert('Salvataggio fallito', e?.message ?? 'Riprova'),
  });

  if (!data) return <div className="card-bd text-center py-10 mute">Caricamento…</div>;

  const merged = { ...data, ...form };

  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 1fr)', maxWidth: 760 }}>
      <div className="card">
        <div className="card-hd">
          <div className="flex items-center gap-3">
            <div className="icon-tile sm" style={{ ['--c' as any]: 'var(--accent)' }}><Icon name="settings" size={14} /></div>
            <div>
              <div className="t-h3">Dati e preferenze dell’azienda</div>
              <div className="mute t-small">Nome, colore, valuta e formati dei documenti</div>
            </div>
          </div>
        </div>
        <div className="card-bd space-y-4">
          <DesktopOnly
            cosa="La modifica delle preferenze organizzative"
            perche="identità, formati e soglie cambiano il comportamento di tutta l'organizzazione; qui sotto puoi leggerne i valori attuali."
          >
          <div className="settings-rows">
            <Setting label="Nome organizzazione">
              <input className="inp" value={merged.organizationName} onChange={(e) => update('organizationName', e.target.value)} />
            </Setting>
            <Setting label="Indirizzo del logo">
              <input className="inp" placeholder="https://…" value={merged.logoUrl ?? ''} onChange={(e) => update('logoUrl', e.target.value)} />
            </Setting>
            <Setting label="Colore primario">
              <div className="flex items-center gap-2">
                <input className="inp mono" style={{ width: 120 }} value={merged.primaryColor} onChange={(e) => update('primaryColor', e.target.value)} />
                <div className="w-8 h-8 rounded border border-line" style={{ background: merged.primaryColor }} />
              </div>
            </Setting>
            <Setting label="Soglia del margine (%)" hint="Sotto questa soglia il progetto va in allerta">
              <input className="inp mono text-right" type="number" min={0} max={100} value={Number(merged.marginThreshold)} onChange={(e) => update('marginThreshold', e.target.value)} />
            </Setting>
            <Setting label="Valuta">
              <select className="inp" value={merged.currency} onChange={(e) => update('currency', e.target.value)}>
                <option>EUR</option><option>USD</option><option>GBP</option><option>CHF</option>
              </select>
            </Setting>
            <Setting label="Fuso orario">
              <select className="inp" value={merged.timezone} onChange={(e) => update('timezone', e.target.value)}>
                <option>Europe/Rome</option><option>Europe/London</option><option>Europe/Berlin</option><option>UTC</option>
              </select>
            </Setting>
            <Setting label="Formato data">
              <select className="inp" value={merged.dateFormat} onChange={(e) => update('dateFormat', e.target.value)}>
                <option>dd/MM/yyyy</option><option>MM/dd/yyyy</option><option>yyyy-MM-dd</option>
              </select>
            </Setting>
            <Setting label="Lingua documenti">
              <select className="inp" value={merged.docLanguage} onChange={(e) => update('docLanguage', e.target.value)}>
                <option value="it">Italiano</option><option value="en">English</option>
              </select>
            </Setting>
            <Setting label="Mese di inizio dell’anno fiscale" hint="1 = gennaio, 4 = aprile">
              <input className="inp mono text-right" type="number" min={1} max={12} value={merged.fiscalYearStart} onChange={(e) => update('fiscalYearStart', Number(e.target.value))} />
            </Setting>
          </div>
          </DesktopOnly>

          <div className="ed-kv m-only settings-mobile-values">
            <SettingValue label="Nome organizzazione" value={merged.organizationName} />
            <SettingValue label="URL logo" value={merged.logoUrl || '—'} />
            <SettingValue label="Colore primario" value={merged.primaryColor} mono />
            <SettingValue label="Soglia margine progetto" value={`${Number(merged.marginThreshold).toFixed(1)}%`} mono />
            <SettingValue label="Valuta" value={merged.currency} mono />
            <SettingValue label="Fuso orario" value={merged.timezone} />
            <SettingValue label="Formato data" value={merged.dateFormat} mono />
            <SettingValue label="Lingua documenti" value={merged.docLanguage === 'it' ? 'Italiano' : 'English'} />
            <SettingValue label="Mese inizio fiscale" value={String(merged.fiscalYearStart)} mono />
          </div>

          {dirty && (
            <div className="flex items-center gap-2 pt-3 border-t border-line">
              <Chip tone="warn">Modifiche non salvate</Chip>
              <div className="spacer" />
              <button className="btn btn-ghost btn-sm" onClick={() => { setForm({}); setDirty(false); }}>Annulla</button>
              <button className="btn btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending}>
                <Icon name="check" size={12} /> Salva preferenze
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Anteprima dei documenti: nascosta il 26/10/2026 (pagina alleggerita, e a
          1280 schiacciava il modulo in tre colonne). Ripeteva logo, colore e
          soglia già scritti nel modulo. Per rimetterla basta mettere MOSTRA_ANTEPRIMA a true. */}
      {MOSTRA_ANTEPRIMA && (
      <div className="card">
        <div className="card-bd text-[13px]">
          <div className="data-hd">
            <span className="t-micro">Anteprima · Documenti</span>
          </div>
          {/* Coppie chiave-valore allineate al pattern ed-kv (hairline condivise) */}
          <div className="ed-kv">
            <div className="ed-kv-row">
              <span className="k">Logo</span>
              <span className="v">
                {merged.logoUrl ? (
                  <img src={merged.logoUrl} alt="Logo" className="max-h-12" />
                ) : (
                  <span className="font-bold uppercase tracking-wider">{merged.organizationName}</span>
                )}
              </span>
            </div>
            <div className="ed-kv-row">
              <span className="k">Colore primario</span>
              <span className="v flex items-center gap-2">
                <span className="w-6 h-6 rounded border border-line inline-block shrink-0" style={{ background: merged.primaryColor }} />
                <span className="mono">{merged.primaryColor}</span>
              </span>
            </div>
            <div className="ed-kv-row">
              <span className="k">Soglia margine</span>
              <span className="v">
                <span className="quadro-value" style={{ color: 'var(--accent-text)' }}>
                  {Number(merged.marginThreshold).toFixed(1)}<span className="u" style={{ marginLeft: 3 }}>%</span>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function Setting({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="settings-row">
      <div>
        <div className="settings-row-label">{label}</div>
        {hint && <div className="settings-row-hint">{hint}</div>}
      </div>
      <div className="settings-row-control">{children}</div>
    </div>
  );
}

function SettingValue({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="ed-kv-row">
      <span className="k">{label}</span>
      <span className={`v ${mono ? 'mono' : ''}`}>{value}</span>
    </div>
  );
}

// =====================================================================
// INTEGRAZIONI
// =====================================================================

/**
 * FattureInCloud e Qonto hanno una card scritta a mano e non stanno nella lista
 * generica delle integrazioni. Le opzioni della query stanno qui, una volta
 * sola, perché le leggono in due: la card e i conteggi in cima alla scheda.
 * React Query, a parità di chiave, non chiama il server due volte.
 */
const ficStatusQuery = {
  queryKey: ['fic-status'] as const,
  queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'),
  refetchOnWindowFocus: false,
};
const qontoStatusQuery = {
  queryKey: ['qonto-status'] as const,
  queryFn: () => api.get<QontoStatusPayload>('/settings/integrations/qonto/status'),
  refetchOnWindowFocus: false,
  retry: 1,
};

function IntegrationsTab() {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  // La scheda dell'Assistente solo agli Amministratori (28/10/2026): le rotte /ai rispondono
  // 403 a tutti gli altri, e la scheda restava una testata vuota.
  const amministratore = useAuthStore((s) => s.user?.role.key === 'SUPER_ADMIN');
  // Riconnessione: campo API key inline (flusso liquid, niente window.prompt).
  // Traccia quale integrazione è in fase di riconnessione + il valore della chiave.
  const [reconnectId, setReconnectId] = useState<string | null>(null);
  const [reconnectKey, setReconnectKey] = useState('');
  const { data } = useQuery({ queryKey: ['integrations'], queryFn: () => api.get<Integration[]>('/settings/integrations') });

  const disconnect = useMutation({
    mutationFn: (id: string) => api.post(`/settings/integrations/${id}/disconnect`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      toast.ok('Integrazione disconnessa');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const reconnect = useMutation({
    mutationFn: ({ id, apiKey }: { id: string; apiKey: string }) =>
      api.patch(`/settings/integrations/${id}`, { apiKey, status: 'ACTIVE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      setReconnectId(null);
      setReconnectKey('');
      toast.ok('Integrazione riconnessa');
    },
    onError: (e: any) => toast.alert('Errore riconnessione', e?.message ?? 'Riprova'),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/settings/integrations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      toast.ok('Integrazione eliminata');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  // I conteggi in cima devono contare anche FattureInCloud e Qonto, che hanno
  // una card loro e non stanno nella lista generica: prima la scheda diceva
  // "0 integrazioni configurate" con due integrazioni connesse dieci pixel sotto.
  const { data: fic } = useQuery(ficStatusQuery);
  const { data: qonto } = useQuery(qontoStatusQuery);
  const nativeConnesse = (fic?.connected ? 1 : 0) + (qonto?.connected ? 1 : 0);

  const items = data ?? [];
  const errorCount = items.filter((i) => i.status === 'ERROR').length;
  const connesse = items.filter((i) => i.status === 'ACTIVE').length + nativeConnesse;

  return (
    <div className="space-y-6">
      <div className="grid grid-3 border border-line">
        <KPI tone="ink"   icon="link"  label="Servizi collegati" value={items.length + nativeConnesse} />
        <KPI tone="ok"    icon="check" label="Funzionano"                 value={connesse} />
        <KPI tone={errorCount > 0 ? 'alert' : 'mute'} icon="alert" label="Con un errore" value={errorCount} attn={errorCount > 0} />
      </div>

      <FicIntegrationCard />
      <QontoIntegrationCard />
      {amministratore && <AssistenteCard />}

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {items.map((i) => (
          <div key={i.id} className="card">
            <div className="card-bd">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="t-h3 truncate">{i.name}</h3>
                <Chip tone={i.status === 'ACTIVE' ? 'ok' : i.status === 'ERROR' ? 'alert' : 'ghost'} dot>{STATUS_LABELS[i.status] ?? i.status}</Chip>
              </div>
              {(KIND_LABELS[i.kind] ?? i.kind) !== i.name && (
                <div className="mute t-small">{KIND_LABELS[i.kind] ?? i.kind}</div>
              )}

              <div className="mt-3 pt-3 border-t border-line space-y-1 text-[13px]">
                <div className="row between"><span className="mute">API key</span>{i.hasKey ? <span className="chip-label"><Icon name="lock" size={10} /> Configurata</span> : <span className="mute-2">Mancante</span>}</div>
                {i.lastSyncAt && <div className="row between"><span className="mute">Ultimo aggiornamento</span><span className="mono">{fmtDate(i.lastSyncAt)}</span></div>}
                {i.connectedBy && <div className="row between"><span className="mute">Connessa da</span><span>{i.connectedBy.firstName[0]}. {i.connectedBy.lastName}</span></div>}
              </div>

              <div className="mt-3 flex gap-1.5 d-only">
                {i.status === 'ACTIVE' ? (
                  <button className="btn btn-sm flex-1 justify-center" onClick={() => disconnect.mutate(i.id)}>
                    <Icon name="unlink" size={10} /> Scollega
                  </button>
                ) : (
                  <button
                    className="btn btn-sm btn-primary flex-1 justify-center"
                    onClick={() => { setReconnectId(i.id); setReconnectKey(''); }}
                  >
                    <Icon name="link" size={10} /> Riconnetti
                  </button>
                )}
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={async () => {
                    const ok = await confirmDialog({
                      title: `Eliminare integrazione ${i.name}?`,
                      body: 'L\'integrazione e la relativa configurazione verranno rimosse.',
                      danger: true,
                      confirmLabel: 'Elimina',
                    });
                    if (ok) del.mutate(i.id);
                  }}
                >
                  <Icon name="x" size={10} />
                </button>
              </div>

              {/* Riconnessione inline: campo API key liquid (sostituisce window.prompt) */}
              {reconnectId === i.id && (
                <div className="mt-3 pt-3 border-t border-line space-y-2">
                  <label className="field-label">Nuova API key per {i.name}</label>
                  <input
                    type="password"
                    className="inp mono"
                    placeholder="Incolla la API key…"
                    value={reconnectKey}
                    onChange={(e) => setReconnectKey(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && reconnectKey.trim() && !reconnect.isPending) reconnect.mutate({ id: i.id, apiKey: reconnectKey.trim() }); }}
                    autoFocus
                  />
                  <div className="flex gap-2 pt-1">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setReconnectId(null); setReconnectKey(''); }}>Annulla</button>
                    <div className="spacer" />
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={!reconnectKey.trim() || reconnect.isPending}
                      onClick={() => reconnect.mutate({ id: i.id, apiKey: reconnectKey.trim() })}
                    >
                      <Icon name="check" size={10} /> Riconnetti
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="col-span-full card">
            {/* "Nessuna integrazione configurata" era falso: FattureInCloud e
                Qonto stanno qui sopra, con la loro card. Questo blocco parla
                solo degli altri servizi. */}
            <EmptyState
              icon="link"
              title="Nessun altro servizio collegato"
              description="Oltre a FattureInCloud e Qonto puoi connettere Slack, Figma, GitHub e gli altri."
              action={
                <button className="btn btn-primary d-only" onClick={() => openModal('newIntegration')}>
                  <Icon name="plus" size={12} /> Nuova integrazione
                </button>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// FATTUREINCLOUD — Card di configurazione "native"
// =====================================================================
interface FicStatus {
  connected: boolean;
  companyId: number | null;
  companyName: string | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
}

interface FicCompany {
  id: number;
  name: string;
  type?: string;
}

function FicIntegrationCard() {
  const qc = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);
  const [pat, setPat] = useState('');
  const [companies, setCompanies] = useState<FicCompany[] | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);

  const { data: status } = useQuery(ficStatusQuery);

  const connect = useMutation({
    mutationFn: () => api.post<{ companies: FicCompany[] }>('/settings/integrations/fic/connect', { pat }),
    onSuccess: (resp) => {
      setCompanies(resp.companies);
      setSelectedCompany(resp.companies[0]?.id ?? null);
      qc.invalidateQueries({ queryKey: ['fic-status'] });
      toast.ok('Token verificato — seleziona l\'azienda');
    },
    onError: (e: any) => toast.alert('Connessione FIC fallita', e?.message ?? 'Riprova'),
  });

  const selectCompany = useMutation({
    mutationFn: () => {
      const c = companies?.find((c) => c.id === selectedCompany);
      if (!c) throw new Error('Seleziona un\'azienda');
      return api.post<FicStatus>('/settings/integrations/fic/select-company', { companyId: c.id, companyName: c.name });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fic-status'] });
      setShowConnect(false);
      setPat('');
      setCompanies(null);
      toast.ok('FattureInCloud connesso');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const disconnect = useMutation({
    mutationFn: () => api.post('/settings/integrations/fic/disconnect'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fic-status'] });
      toast.ok('FattureInCloud disconnesso');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const isConnected = status?.connected;

  return (
    <div className="card">
      <div className="card-hd flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm" style={{ ['--c' as any]: 'var(--accent)' }}><Icon name="invoice" size={14} /></div>
          <div className="flex items-center gap-2">
            <div className="t-h3">FattureInCloud</div>
            <Chip tone={isConnected ? 'ok' : 'ghost'} dot>{isConnected ? 'Connesso' : 'Non connesso'}</Chip>
          </div>
        </div>
        {isConnected ? (
          <button className="btn btn-sm d-only" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
            <Icon name="unlink" size={10} /> Scollega
          </button>
        ) : (
          <button className="btn btn-sm btn-primary d-only" onClick={() => setShowConnect(true)}>
            <Icon name="link" size={10} /> Connetti
          </button>
        )}
      </div>

      <div className="card-bd text-[13px] mute">
        {isConnected ? (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="t-micro">Azienda su Fatture in Cloud</div>
              <div className="text-ink font-semibold">{status?.companyName ?? '—'}</div>
              <div className="mono text-[12px] mute">ID {status?.companyId}</div>
            </div>
            <div>
              <div className="t-micro">Connesso il</div>
              <div className="text-ink">{status?.connectedAt ? fmtDate(status.connectedAt) : '—'}</div>
            </div>
            <div>
              <div className="t-micro">Ultimo aggiornamento</div>
              <div className="text-ink">{status?.lastSyncAt ? fmtDate(status.lastSyncAt) : '—'}</div>
            </div>
          </div>
        ) : (
          <div>
            Connetti FattureInCloud per emettere fatture e preventivi direttamente da Masamune OS.
            <br />
            Crea un <strong>Personal Access Token</strong> qui:{' '}
            <a href="https://compute.fattureincloud.it/dash/profile/settings/manage-pats" target="_blank" rel="noreferrer" className="underline" style={{ color: 'var(--accent)' }}>
              compute.fattureincloud.it/dash/profile/settings/manage-pats
            </a>
          </div>
        )}
      </div>

      {/* Connect flow: incolla PAT → mostra companies → seleziona */}
      {showConnect && (
        <div className="card-bd border-t border-line">
          {!companies ? (
            <div className="space-y-2">
              <label className="field-label">
                Personal Access Token
                <Helper placement="right">
                  <strong>Cos'è il PAT?</strong>
                  <div className="mt-1">È una chiave segreta che FattureInCloud ti dà nelle impostazioni del tuo profilo, senza condividere la password. Permette a Masamune OS di emettere fatture per conto tuo.</div>
                  <div className="mt-2"><strong>Come crearlo:</strong> vai su FIC → profilo → "Manage Personal Access Tokens" → "Genera nuovo". Lo incolli qui sotto.</div>
                </Helper>
              </label>
              <input
                type="password"
                className="inp mono"
                placeholder="paste your PAT here"
                value={pat}
                onChange={(e) => setPat(e.target.value)}
                autoFocus
              />
              <div className="field-hint">Il token viene cifrato (AES-256-GCM) e salvato solo lato server.</div>
              <div className="flex gap-2 pt-2">
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowConnect(false); setPat(''); }}>Annulla</button>
                <div className="spacer" />
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!pat || connect.isPending}
                  onClick={() => connect.mutate()}
                >
                  <Icon name="link" size={10} /> Verifica e continua
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="field-label">Seleziona l'azienda da utilizzare</label>
              <select className="inp" value={selectedCompany ?? ''} onChange={(e) => setSelectedCompany(Number(e.target.value))}>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} {c.type ? `(${c.type})` : ''}</option>
                ))}
              </select>
              <div className="flex gap-2 pt-2">
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowConnect(false); setPat(''); setCompanies(null); }}>Annulla</button>
                <div className="spacer" />
                <button className="btn btn-primary btn-sm" onClick={() => selectCompany.mutate()} disabled={!selectedCompany || selectCompany.isPending}>
                  <Icon name="check" size={10} /> Conferma azienda
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// QONTO — login + secret (API key) → verifica → stato connesso / ultima sync
// I segreti non tornano mai al client: lo stato espone solo il nome
// dell'organizzazione e le date. Sync manuale da qui o da /admin.
// =====================================================================
interface QontoStatusPayload {
  connected: boolean;
  organizationName?: string | null;
  orgSlug?: string | null;
  login?: string | null;
  connectedAt?: string | null;
  lastSyncAt?: string | null;
  accounts?: Array<{ id: string; name: string; balance: number }> | null;
}

function QontoIntegrationCard() {
  const qc = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);
  const [login, setLogin] = useState('');
  const [secret, setSecret] = useState('');

  const { data: status } = useQuery(qontoStatusQuery);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['qonto-status'] });
    qc.invalidateQueries({ queryKey: ['admin'] });
  };

  const connect = useMutation({
    mutationFn: () => api.post<QontoStatusPayload>('/settings/integrations/qonto/connect', { login: login.trim(), secret: secret.trim() }),
    onSuccess: (r) => {
      invalidate();
      setShowConnect(false);
      setLogin('');
      setSecret('');
      toast.ok('Qonto connesso', r?.organizationName ?? undefined);
    },
    onError: (e: any) => toast.alert('Connessione Qonto fallita', e?.message ?? 'Controlla login e secret'),
  });

  const sync = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { created?: number; updated?: number } } | undefined>('/admin/sync/qonto'),
    onSuccess: (r) => {
      invalidate();
      const s = r?.stats;
      toast.ok('Qonto aggiornato', s ? `${s.created ?? 0} movimenti nuovi · ${s.updated ?? 0} aggiornati` : undefined);
    },
    onError: (e: any) => toast.alert('Aggiornamento fallito', e?.message ?? 'Riprova'),
  });

  const disconnect = useMutation({
    mutationFn: async () => {
      const ok = await confirmDialog({
        title: 'Scollegare Qonto?',
        body: 'Saldi e movimenti già importati restano; smettono solo di aggiornarsi.',
        confirmLabel: 'Scollega',
        icon: 'unlink',
      });
      if (!ok) return null;
      await api.post('/settings/integrations/qonto/disconnect');
      return true;
    },
    onSuccess: (done) => { if (!done) return; invalidate(); toast.ok('Qonto scollegato'); },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const isConnected = !!status?.connected;

  return (
    <div className="card">
      <div className="card-hd flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="icon-tile sm" style={{ ['--c' as any]: 'var(--accent)' }}><Icon name="landmark" size={14} /></div>
          <div className="flex items-center gap-2">
            <div className="t-h3">Qonto</div>
            <Chip tone={isConnected ? 'ok' : 'ghost'} dot>{isConnected ? 'Connesso' : 'Non connesso'}</Chip>
          </div>
        </div>
        {isConnected ? (
          <div className="flex gap-2 d-only">
            <button className="btn btn-sm" onClick={() => sync.mutate()} disabled={sync.isPending}>
              <Icon name="refresh" size={10} /> {sync.isPending ? 'Aggiorno…' : 'Aggiorna ora'}
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
              <Icon name="unlink" size={10} /> Scollega
            </button>
          </div>
        ) : (
          <button className="btn btn-sm btn-primary d-only" onClick={() => setShowConnect(true)}>
            <Icon name="link" size={10} /> Connetti
          </button>
        )}
      </div>

      <div className="card-bd text-[13px] mute">
        {isConnected ? (
          <div className="grid grid-cols-3 gap-4">
            <div className="min-w-0">
              <div className="t-micro">Conti collegati</div>
              <div className="text-ink font-semibold">{status?.accounts?.length ?? 0} cont{(status?.accounts?.length ?? 0) === 1 ? 'o' : 'i'}</div>
              {/* Lo slug Qonto è la ragione sociale per esteso: si tronca, il titolo lo mostra intero */}
              <div className="text-[12px] mute truncate" title={status?.organizationName ?? status?.orgSlug ?? undefined}>
                {status?.accounts?.length ? status.accounts.map((a) => a.name).join(', ') : (status?.organizationName ?? status?.orgSlug ?? '—')}
              </div>
            </div>
            <div>
              <div className="t-micro">Connesso il</div>
              <div className="text-ink">{status?.connectedAt ? fmtDate(status.connectedAt) : '—'}</div>
            </div>
            <div>
              <div className="t-micro">Ultimo aggiornamento</div>
              <div className="text-ink">{status?.lastSyncAt ? fmtDate(status.lastSyncAt) : 'mai'}</div>
            </div>
          </div>
        ) : (
          <div>
            Collega Qonto per vedere saldi e movimenti in Amministrazione e abbinarli a incassi e uscite.
            <br />
            Le credenziali sono in Qonto: <strong>Impostazioni → Integrazioni e offerte → Chiave API</strong> (login + secret key). Servono solo permessi di lettura.
          </div>
        )}
      </div>

      {showConnect && (
        <div className="card-bd border-t border-line">
          <div className="space-y-2">
            <label className="field-label">
              Login
              <Helper placement="right">
                <strong>Cos'è il login Qonto?</strong>
                <div className="mt-1">È l'identificativo dell'organizzazione (es. <span className="mono">masamune-1234</span>) che Qonto mostra accanto alla secret key nella pagina Chiave API.</div>
              </Helper>
            </label>
            <input className="inp mono" placeholder="organizzazione-1234" value={login} onChange={(e) => setLogin(e.target.value)} autoFocus />
            <label className="field-label">Secret key</label>
            <input type="password" className="inp mono" placeholder="incolla qui la secret key" value={secret} onChange={(e) => setSecret(e.target.value)} />
            <div className="field-hint">Cifrata (AES-256-GCM) e salvata solo lato server. Non viene mai mostrata di nuovo.</div>
            <div className="flex gap-2 pt-2">
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowConnect(false); setLogin(''); setSecret(''); }}>Annulla</button>
              <div className="spacer" />
              <button className="btn btn-primary btn-sm" disabled={!login.trim() || !secret.trim() || connect.isPending} onClick={() => connect.mutate()}>
                <Icon name="link" size={10} /> {connect.isPending ? 'Verifico…' : 'Verifica e collega'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
